import { useState, useCallback, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import {
  BarChart3, TrendingDown, TrendingUp, AlertTriangle, CheckCircle,
  Euro, Zap, Flame, Download, FileSignature, Mail, Send,
  SlidersHorizontal, Shield, Calculator, FileText, Loader2, ChevronRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ label, value, sublabel, color = "text-gray-900", icon: Icon, small }) {
  return (
    <div className={`p-3 rounded-xl bg-white border ${small ? '' : 'shadow-sm'}`}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className={`w-3.5 h-3.5 ${color}`} />}
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-xl font-black ${color}`}>{value}</p>
      {sublabel && <p className="text-[10px] text-gray-400 mt-0.5">{sublabel}</p>}
    </div>
  );
}

export default function ComparisonEngine({ billData, offers, marketPrices }) {
  const [spread, setSpread] = useState(0.013);
  const [pcv, setPcv] = useState(8.50);
  const [selectedOfferIds, setSelectedOfferIds] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mesiPeriodo, setMesiPeriodo] = useState(1);
  // Switch-In state
  const [switchInStep, setSwitchInStep] = useState(null); // null, "generating", "generated"
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [contrattoId, setContrattoId] = useState(null);

  const tipo = billData?.tipo || "luce";
  const consumo = parseFloat(billData?.consumo || 0);
  const totale = parseFloat(billData?.totale || 0);
  const isLuce = tipo === "luce";

  // Real-time local calculation as sliders move
  const liveCalc = useMemo(() => {
    if (!consumo || !totale) return null;
    const indice = isLuce ? 0.11 : 0.40;
    const consumoMensile = consumo / Math.max(mesiPeriodo, 1);
    const spesaMensile = totale / Math.max(mesiPeriodo, 1);
    const prezzo8e = indice + spread;
    const costo8e = (consumoMensile * prezzo8e) + pcv;
    const risparmio = spesaMensile - costo8e;
    const margine = (consumoMensile * spread) + pcv;
    return {
      spesaMensile: spesaMensile.toFixed(2),
      costo8e: costo8e.toFixed(2),
      risparmio: risparmio.toFixed(2),
      risparmioAnnuo: (risparmio * 12).toFixed(0),
      risparmioPct: spesaMensile > 0 ? ((risparmio / spesaMensile) * 100).toFixed(1) : "0",
      margine: margine.toFixed(2),
      margineAnnuo: (margine * 12).toFixed(0),
      isWinback: risparmio < 0,
      prezzoUnitario: prezzo8e.toFixed(4),
    };
  }, [consumo, totale, spread, pcv, mesiPeriodo, isLuce]);

  const handleCalcola = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/comparazione/calcola`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          bill_data: { ...billData, mesi_periodo: mesiPeriodo },
          custom_spread: spread,
          custom_pcv: pcv,
          offer_ids: selectedOfferIds
        })
      });
      if (!res.ok) throw new Error("Errore calcolo");
      const data = await res.json();
      setComparison(data);
      if (data.winback?.is_peggiorativa) {
        toast.warning(data.winback.alert);
      } else {
        toast.success(`Risparmio stimato: €${data.offerta_personalizzata?.risparmio_mensile}/mese`);
      }
    } catch (e) {
      toast.error("Errore nel calcolo comparazione");
    } finally {
      setLoading(false);
    }
  }, [billData, spread, pcv, selectedOfferIds, mesiPeriodo]);

  const handleGeneraContratto = useCallback(async (offer) => {
    setSelectedOffer(offer);
    setSwitchInStep("generating");
    try {
      const res = await fetch(`${API}/admin/switch-in/genera-contratto`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          cliente: {
            intestatario: billData?.cliente || billData?.intestatario || "",
            codice_fiscale: billData?.codice_fiscale || billData?.pod_pdr || "",
            indirizzo: billData?.indirizzo || "",
          },
          offerta: {
            nome: offer.nome,
            spread: offer.spread,
            pcv: offer.pcv,
            costo_mensile: offer.costo_mensile_stimato,
            risparmio_mensile: offer.risparmio_mensile,
            risparmio_annuo: offer.risparmio_annuo,
          },
          dati_fornitura: {
            tipo: tipo,
            codice_punto: billData?.pod_pdr || "",
            indirizzo_fornitura: billData?.indirizzo || "",
            potenza_impegnata: billData?.potenza_impegnata || "",
            tipologia_uso: billData?.tipologia_uso || "domestico_residente",
            consumo_annuo: consumo * (12 / Math.max(mesiPeriodo, 1)),
          }
        })
      });
      if (!res.ok) throw new Error("Errore generazione");
      const cid = res.headers.get("X-Contract-ID") || `CTR-${Date.now()}`;
      setContrattoId(cid);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `8energy_contratto_${cid}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setSwitchInStep("generated");
      toast.success(`Contratto ${cid} generato e scaricato`);
    } catch (e) {
      toast.error("Errore generazione contratto");
      setSwitchInStep(null);
    }
  }, [billData, tipo, consumo, mesiPeriodo]);

  if (!billData || !consumo) {
    return (
      <div className="text-center py-20" data-testid="comparison-engine">
        <Calculator className="w-16 h-16 mx-auto text-gray-200 mb-4" />
        <p className="text-lg font-semibold text-gray-400" data-testid="no-bill-data-message">Nessun dato bolletta caricato</p>
        <p className="text-sm text-gray-300 mt-1">Usa Smart OCR per estrarre i dati dalla bolletta del cliente</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="comparison-engine">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-3" data-testid="comparison-engine-title">
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-2 rounded-xl">
              <Calculator className="w-5 h-5" />
            </span>
            Motore di Comparazione
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">Calcolo dinamico con PUN/PSV + marginalità in tempo reale</p>
        </div>
        <Button onClick={handleCalcola} disabled={loading} data-testid="calcola-btn"
          className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calculator className="w-4 h-4 mr-2" />}
          Calcola Comparazione
        </Button>
      </div>

      {/* Bill Summary + Normalization */}
      <Card className="border-2 border-emerald-200 bg-emerald-50/30">
        <CardContent className="py-4">
          <div className="grid grid-cols-6 gap-3 items-center">
            <div className="col-span-4 grid grid-cols-4 gap-3">
              <StatCard label="Fornitore" value={billData.fornitore || "N/A"} icon={isLuce ? Zap : Flame} color={isLuce ? "text-yellow-600" : "text-orange-600"} small />
              <StatCard label="Consumo" value={`${consumo} ${isLuce ? 'kWh' : 'Smc'}`} icon={BarChart3} color="text-blue-600" small />
              <StatCard label="Totale Bolletta" value={`€${totale}`} icon={Euro} color="text-gray-900" small />
              <div className="p-3 rounded-xl bg-white border">
                <Label className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Mesi Periodo</Label>
                <Input type="number" min={1} max={12} value={mesiPeriodo} onChange={e => setMesiPeriodo(parseInt(e.target.value) || 1)}
                  className="h-8 mt-1 text-center font-bold" data-testid="mesi-periodo-input" />
              </div>
            </div>
            <div className="col-span-2 grid grid-cols-2 gap-3">
              <StatCard label="Consumo Annuo" value={`${Math.round(consumo * 12 / Math.max(mesiPeriodo, 1))}`} sublabel={isLuce ? "kWh/anno" : "Smc/anno"} icon={TrendingUp} color="text-purple-600" small />
              <StatCard label="Spesa Annua" value={`€${Math.round(totale * 12 / Math.max(mesiPeriodo, 1))}`} sublabel="stimata" icon={Euro} color="text-red-600" small />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* LEFT: Dynamic Sliders + Margin Panel */}
        <div className="space-y-4">
          <Card className="border-2 border-purple-200">
            <CardHeader className="py-3 px-4 bg-purple-50">
              <CardTitle className="flex items-center gap-2 text-sm text-purple-700">
                <SlidersHorizontal className="w-4 h-4" /> Pannello Marginalità
              </CardTitle>
            </CardHeader>
            <CardContent className="py-4 space-y-5">
              {/* Spread Slider */}
              <div data-testid="spread-slider-container">
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-xs font-bold text-gray-600">Spread ({isLuce ? '€/kWh' : '€/Smc'})</Label>
                  <span className="text-sm font-black text-purple-700">{spread.toFixed(3)}</span>
                </div>
                <Slider
                  value={[spread * 1000]}
                  onValueChange={([v]) => setSpread(v / 1000)}
                  min={0}
                  max={50}
                  step={1}
                  className="[&_[role=slider]]:bg-purple-600"
                  data-testid="spread-slider"
                />
                <div className="flex justify-between text-[9px] text-gray-400 mt-1">
                  <span>0.000</span><span>0.025</span><span>0.050</span>
                </div>
              </div>

              {/* PCV Slider */}
              <div data-testid="pcv-slider-container">
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-xs font-bold text-gray-600">Quota Fissa PCV (€/mese)</Label>
                  <span className="text-sm font-black text-purple-700">€{pcv.toFixed(2)}</span>
                </div>
                <Slider
                  value={[pcv * 10]}
                  onValueChange={([v]) => setPcv(v / 10)}
                  min={0}
                  max={200}
                  step={5}
                  className="[&_[role=slider]]:bg-purple-600"
                  data-testid="pcv-slider"
                />
                <div className="flex justify-between text-[9px] text-gray-400 mt-1">
                  <span>€0</span><span>€10</span><span>€20</span>
                </div>
              </div>

              {/* Real-time ROI */}
              {liveCalc && (
                <div className="space-y-3 pt-3 border-t" data-testid="live-roi">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Real-time ROI</h4>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-200 text-center">
                      <p className="text-[9px] text-emerald-600 font-bold uppercase">Risparmio Cliente</p>
                      <p className={`text-lg font-black ${parseFloat(liveCalc.risparmio) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>€{liveCalc.risparmio}</p>
                      <p className="text-[9px] text-gray-400">/mese ({liveCalc.risparmioPct}%)</p>
                    </div>
                    <div className="p-2 rounded-lg bg-purple-50 border border-purple-200 text-center">
                      <p className="text-[9px] text-purple-600 font-bold uppercase">Margine Azienda</p>
                      <p className="text-lg font-black text-purple-700">€{liveCalc.margine}</p>
                      <p className="text-[9px] text-gray-400">/mese (€{liveCalc.margineAnnuo}/anno)</p>
                    </div>
                  </div>

                  {/* Win-back Alert */}
                  <AnimatePresence>
                    {liveCalc.isWinback && (
                      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                        className="p-3 bg-red-50 border-2 border-red-300 rounded-xl" data-testid="winback-alert"
                      >
                        <div className="flex items-center gap-2 text-red-700 font-bold text-xs">
                          <AlertTriangle className="w-4 h-4" /> OFFERTA PEGGIORATIVA
                        </div>
                        <p className="text-[10px] text-red-600 mt-1">
                          Il cliente pagherebbe €{Math.abs(parseFloat(liveCalc.risparmio)).toFixed(2)}/mese in più. Riduci spread o PCV.
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Offer Selection */}
          <Card className="border-2 border-gray-200">
            <CardHeader className="py-3 px-4 bg-gray-50">
              <CardTitle className="text-sm flex items-center gap-2 text-gray-600">
                <BarChart3 className="w-4 h-4" /> Confronta con Offerte
              </CardTitle>
            </CardHeader>
            <CardContent className="py-3 max-h-[250px] overflow-y-auto">
              {offers.filter(o => o.tipo === tipo).map(offer => (
                <label key={offer.offer_id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                  <Checkbox
                    checked={selectedOfferIds.includes(offer.offer_id)}
                    onCheckedChange={(checked) => {
                      setSelectedOfferIds(prev =>
                        checked ? [...prev, offer.offer_id] : prev.filter(id => id !== offer.offer_id)
                      );
                    }}
                  />
                  <span className="text-sm">{offer.nome}</span>
                  <span className="text-[10px] text-gray-400 ml-auto">{offer.spread || offer.prezzo_kwh || "—"}</span>
                </label>
              ))}
              {offers.filter(o => o.tipo === tipo).length === 0 && (
                <p className="text-sm text-gray-400 text-center py-4">Nessuna offerta {tipo} disponibile</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* CENTER + RIGHT: Results + Switch-In */}
        <div className="lg:col-span-2 space-y-4">
          {/* Comparison Results Table */}
          {comparison?.offerte_comparate?.length > 0 ? (
            <Card className="border-2 border-emerald-200">
              <CardHeader className="py-3 px-4 bg-emerald-50">
                <CardTitle className="text-sm flex items-center gap-2 text-emerald-700">
                  <TrendingDown className="w-4 h-4" /> Ranking Offerte per Risparmio
                </CardTitle>
              </CardHeader>
              <CardContent className="py-3 px-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="comparison-results-table">
                    <thead>
                      <tr className="border-b bg-emerald-50/50">
                        <th className="px-3 py-2 text-left">#</th>
                        <th className="px-3 py-2 text-left">Offerta</th>
                        <th className="px-3 py-2 text-right">Spread</th>
                        <th className="px-3 py-2 text-right">PCV</th>
                        <th className="px-3 py-2 text-right">Costo/mese</th>
                        <th className="px-3 py-2 text-right">Risparmio/mese</th>
                        <th className="px-3 py-2 text-right">Risparmio/anno</th>
                        <th className="px-3 py-2 text-right">Margine/mese</th>
                        <th className="px-3 py-2 text-center">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.offerte_comparate.map((oc, idx) => (
                        <tr key={oc.offer_id} className={`border-b ${idx === 0 ? 'bg-emerald-50' : ''} ${oc.is_winback ? 'bg-red-50' : ''}`}>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${idx === 0 && !oc.is_winback ? 'bg-emerald-600 text-white' : 'bg-gray-100'}`}>
                              {idx + 1}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-medium">{oc.nome}</td>
                          <td className="px-3 py-2 text-right font-mono">{oc.spread?.toFixed(3)}</td>
                          <td className="px-3 py-2 text-right">€{oc.pcv?.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right font-bold">€{oc.costo_mensile_stimato}</td>
                          <td className={`px-3 py-2 text-right font-bold ${oc.risparmio_mensile >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                            {oc.risparmio_mensile >= 0 ? '+' : ''}€{oc.risparmio_mensile}
                            {oc.is_winback && <AlertTriangle className="w-3 h-3 inline ml-1 text-red-500" />}
                          </td>
                          <td className={`px-3 py-2 text-right font-bold ${oc.risparmio_annuo >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                            €{oc.risparmio_annuo}
                          </td>
                          <td className="px-3 py-2 text-right text-purple-700 font-bold">€{oc.margine_mensile}</td>
                          <td className="px-3 py-2 text-center">
                            <Button size="sm" variant="ghost" className="h-7 text-[10px]"
                              data-testid={`switch-in-btn-${idx}`}
                              disabled={oc.is_winback}
                              onClick={() => handleGeneraContratto(oc)}>
                              <FileSignature className="w-3 h-3 mr-1" /> Switch-In
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : comparison ? (
            <Card className="border-2 border-emerald-200">
              <CardContent className="py-8 text-center">
                <CheckCircle className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
                <p className="font-semibold">Calcolo completato</p>
                <p className="text-sm text-gray-500 mt-1">Seleziona offerte a sinistra per il confronto dettagliato</p>
              </CardContent>
            </Card>
          ) : null}

          {/* Custom Offer Summary (from sliders) */}
          {comparison?.offerta_personalizzata && (
            <Card className="border-2 border-purple-200" data-testid="custom-offer-summary">
              <CardHeader className="py-3 px-4 bg-purple-50">
                <CardTitle className="text-sm flex items-center gap-2 text-purple-700">
                  <SlidersHorizontal className="w-4 h-4" /> Offerta Personalizzata (Slider)
                </CardTitle>
              </CardHeader>
              <CardContent className="py-3">
                <div className="grid grid-cols-4 gap-3">
                  <StatCard label="Prezzo Unitario" value={`€${comparison.offerta_personalizzata.prezzo_unitario}`} sublabel={`${isLuce ? 'PUN' : 'PSV'} + spread`} icon={Calculator} color="text-purple-700" small />
                  <StatCard label="Costo Mensile" value={`€${comparison.offerta_personalizzata.costo_mensile_stimato}`} icon={Euro} color="text-gray-800" small />
                  <StatCard label="Risparmio Mensile" value={`€${comparison.offerta_personalizzata.risparmio_mensile}`} sublabel={`${comparison.offerta_personalizzata.risparmio_percentuale}%`} icon={TrendingDown}
                    color={comparison.offerta_personalizzata.risparmio_mensile >= 0 ? "text-emerald-700" : "text-red-600"} small />
                  <StatCard label="Margine Annuo" value={`€${comparison.marginalita.margine_annuo}`} sublabel={`€${comparison.marginalita.margine_mensile}/mese`} icon={TrendingUp} color="text-purple-700" small />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Switch-In Workflow */}
          <AnimatePresence>
            {(switchInStep || selectedOffer) && (
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
                <Card className="border-2 border-orange-200" data-testid="switch-in-panel">
                  <CardHeader className="py-3 px-4 bg-orange-50">
                    <CardTitle className="text-sm flex items-center gap-2 text-orange-700">
                      <FileSignature className="w-4 h-4" /> Automazione Switch-In
                      {selectedOffer && <span className="text-[10px] bg-orange-200 text-orange-700 px-2 py-0.5 rounded-full ml-2">{selectedOffer.nome}</span>}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="py-4 space-y-4">
                    {/* Pipeline Steps */}
                    <div className="flex items-center gap-2" data-testid="switch-in-pipeline">
                      {["Genera Contratto", "Scarica PDF"].map((step, i) => {
                        const stepStates = { "generating": 0, "generated": 1 };
                        const current = stepStates[switchInStep] ?? -1;
                        const isDone = i < current;
                        const isActive = i === current;
                        return (
                          <div key={step} className="flex items-center flex-1">
                            <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold w-full
                              ${isDone ? 'bg-emerald-100 text-emerald-700' : ''}
                              ${isActive ? 'bg-orange-500 text-white shadow-md' : ''}
                              ${!isDone && !isActive ? 'bg-gray-100 text-gray-400' : ''}
                            `}>
                              {isDone && <CheckCircle className="w-3 h-3 shrink-0" />}
                              {isActive && <Loader2 className="w-3 h-3 animate-spin shrink-0" />}
                              <span className="truncate">{step}</span>
                            </div>
                            {i < 1 && <ChevronRight className="w-3 h-3 shrink-0 mx-0.5 text-gray-300" />}
                          </div>
                        );
                      })}
                    </div>

                    {contrattoId && (
                      <div className="p-2 bg-orange-50 rounded-lg text-[11px] font-mono text-orange-700 flex items-center gap-2">
                        <Shield className="w-3.5 h-3.5" />
                        Contratto: {contrattoId}
                        {switchInStep === "generated" && <span className="ml-auto text-emerald-600 font-bold flex items-center gap-1"><CheckCircle className="w-3 h-3" /> PDF scaricato</span>}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
